import { TourProvider, useTour } from '@reactour/tour';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { CustomNavigation } from '@/features/guidedTour/components/CustomNavigation';
import type { TourComponentProps } from '@/features/guidedTour/types';
import {
    trackTourComplete,
    trackTourDecline,
    trackTourDrop,
    trackTourStart,
} from '@/features/guidedTour/utils/analytics';
import { getSteps } from '@/features/guidedTour/utils/steps';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';

const RENDER_DELAY = 300;

export const TourComponent = ({ formattedSteps }: TourComponentProps) => {
    const { setIsOpen, setCurrentStep } = useTour();
    const navigate = useNavigate();
    const [showDialog, setShowDialog] = useState(false);
    const [searchParams] = useSearchParams();
    const isRfidModalOpen = searchParams.get('modal') === 'orderCards';

    const checkTourStatus = useCallback(() => {
        const tourInteraction = localStorage.getItem('tourInteraction');
        const lastStep = localStorage.getItem('lastStep');

        if (isRfidModalOpen) {
            // Don't show the tour if RFID modal is open
            return;
        }

        if (!tourInteraction || (tourInteraction === 'started' && !lastStep)) {
            setShowDialog(true);
        } else if (tourInteraction === 'started' && lastStep) {
            const stepIndex = Number(lastStep);
            if (stepIndex < formattedSteps.length) {
                setCurrentStep(stepIndex);
                navigate(formattedSteps[stepIndex].route);
                setTimeout(() => setIsOpen(true), RENDER_DELAY);
            } else {
                setShowDialog(true);
            }
        }
    }, [setIsOpen, setCurrentStep, isRfidModalOpen]);

    useEffect(() => {
        checkTourStatus();
    }, [checkTourStatus]);

    useEffect(() => {
        // Close the tour if RFID order modal opens while tour is active
        if (isRfidModalOpen) {
            setIsOpen(false);
        }
    }, [isRfidModalOpen, setIsOpen]);

    const startTour = () => {
        setShowDialog(false);
        setCurrentStep(0);
        localStorage.setItem('tourInteraction', 'started');
        localStorage.setItem('lastStep', '0');
        navigate(formattedSteps[0].route);

        const isManualStart = localStorage.getItem('tourPreviouslyCompleted') === 'true';
        trackTourStart(isManualStart ? 'manual' : 'initial');

        setTimeout(() => setIsOpen(true), RENDER_DELAY);
    };

    const skipTour = () => {
        setShowDialog(false);
        localStorage.setItem('tourInteraction', 'skipped');
        localStorage.removeItem('lastStep');
        trackTourDecline('skip');
    };

    const closeModal = () => {
        setShowDialog(false);
        localStorage.setItem('tourInteraction', 'closed');
        trackTourDecline('close');
    };

    return (
        <Dialog
            show={showDialog}
            onClose={closeModal}
            title={<FormattedMessage id='guidedTour.modal.title' />}
            body={
                <div>
                    <img className='width-100pct margin-bottom-25' src='/tour.svg' alt='Guided Tour' />
                    <FormattedMessage
                        id='guidedTour.modal.body'
                        values={{
                            b: chunks => <b>{chunks}</b>,
                        }}
                    />
                </div>
            }
            footer={
                <div className='display-flex justify-content-end gap-10 margin-top-10'>
                    <Button onClick={skipTour}>
                        <FormattedMessage id='guidedTour.modal.skip' />
                    </Button>
                    <Button onClick={startTour} bsStyle='primary'>
                        <FormattedMessage id='guidedTour.modal.start' />
                    </Button>
                </div>
            }
        />
    );
};

export const GuidedTourModal: React.FC = () => {
    const [tourStartTime] = useState<number>(Date.now());
    const { value: isCardOrderHistoryEnabled, loading: isFeatureToggleLoading } = useFeatureToggle(
        FeatureToggles.ENABLE_CARD_ORDER_HISTORY
    );

    const formattedSteps = useMemo(
        () =>
            getSteps(Boolean(isCardOrderHistoryEnabled)).map(step => ({
                ...step,
                content: () => (
                    <>
                        <h5 data-testid='guided-tour-title'>
                            <FormattedMessage id={`${step.key}.title`} />
                        </h5>
                        <p data-testid='guided-tour-body'>
                            <FormattedMessage id={`${step.key}.body`} />
                        </p>
                    </>
                ),
            })),
        [isCardOrderHistoryEnabled]
    );

    const handleTourEnd = (currentStep: number) => {
        const timeSpent = Math.floor((Date.now() - tourStartTime) / 1000);

        if (currentStep === formattedSteps.length - 1) {
            trackTourComplete(timeSpent);
            localStorage.setItem('tourPreviouslyCompleted', 'true');
        } else {
            trackTourDrop(currentStep, formattedSteps.length, timeSpent);
        }
    };

    // Wait for the feature toggle to resolve before rendering the tour. While the
    // flag is loading, useFeatureFlag returns the default (false), which would build
    // the steps with the legacy Administration cards route (/administration/cards)
    // and navigate the user to the old link in production.
    if (isFeatureToggleLoading) {
        return null;
    }

    return (
        <TourProvider
            steps={formattedSteps}
            styles={{
                popover: base => ({
                    ...base,
                    marginTop: '16px',
                    borderRadius: '4px',
                    '--reactour-accent': 'var(--brand-secondary)',
                }),
                maskWrapper: base => ({
                    ...base,
                    color: 'rgba(42, 55, 64, 0.4)',
                }),
                badge: base => ({
                    ...base,
                    left: '-16px',
                    top: '-16px',
                    borderRadius: '100%',
                    padding: 0,
                    width: 30,
                    height: 30,
                    font: 'inherit',
                    paddingTop: '4px',
                }),
                close: base => ({
                    ...base,
                    right: 16,
                    left: 'auto',
                    top: 16,
                    width: 16,
                    height: 16,
                }),
            }}
            beforeClose={() => {
                const currentStep = localStorage.getItem('lastStep');
                handleTourEnd(Number(currentStep));
                if (currentStep) {
                    localStorage.setItem('lastStep', currentStep);
                }
            }}
            onClickMask={({ setIsOpen, currentStep }) => {
                handleTourEnd(currentStep);
                setIsOpen(false);
                localStorage.setItem('lastStep', currentStep.toString());
            }}
            onClickClose={({ setIsOpen, currentStep }) => {
                handleTourEnd(currentStep);
                setIsOpen(false);
                localStorage.removeItem('lastStep');
                localStorage.setItem('tourInteraction', 'closed');
            }}
            components={{ Navigation: CustomNavigation }}
        >
            <TourComponent formattedSteps={formattedSteps} />
        </TourProvider>
    );
};
